import type React from 'react'
import type { TypographyOptions } from '@mui/material/styles/createTypography'

// ── Module augmentation — register custom variants with MUI ─────────────────
declare module '@mui/material/styles' {
  interface TypographyVariants {
    bodyLight1: React.CSSProperties
    bodyLight2: React.CSSProperties
    bodyLight3: React.CSSProperties
    bodyRegular1: React.CSSProperties
    bodyRegular2: React.CSSProperties
    bodyRegular3: React.CSSProperties
    bodyMedium1: React.CSSProperties
    bodyMedium2: React.CSSProperties
    bodyMedium3: React.CSSProperties
    bodyDark1: React.CSSProperties
    bodyDark2: React.CSSProperties
    bodyDark3: React.CSSProperties
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface TypographyVariantsOptions extends Partial<TypographyVariants> {}
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    bodyLight1: true
    bodyLight2: true
    bodyLight3: true
    bodyRegular1: true
    bodyRegular2: true
    bodyRegular3: true
    bodyMedium1: true
    bodyMedium2: true
    bodyMedium3: true
    bodyDark1: true
    bodyDark2: true
    bodyDark3: true
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────
export const FONT_FAMILY = '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'

const createCustomVariant = (
  fontSize: number,
  fontWeight: number,
  additionalStyles?: React.CSSProperties,
): React.CSSProperties => ({
  fontSize,
  fontWeight,
  display: 'block',
  color: 'neutral.80',
  ...additionalStyles,
})

// ── Font weights (reusable across theme) ────────────────────────────────────
export const fontWeights = {
  light: 400,
  regular: 500,
  medium: 600,
  bold: 700,
} as const

// ── Typography config ───────────────────────────────────────────────────────
export const typography: TypographyOptions = {
  fontFamily: FONT_FAMILY,

  // ── Headings (each individually styled) ─────────────────────────────────
  h1: {
    fontSize: 32,
    fontWeight: fontWeights.bold,
    lineHeight: '2.5rem',
    fontStyle: 'normal',
    letterSpacing: '-0.02em',
    textTransform: 'none',
  },
  h2: {
    fontSize: 30,
    fontWeight: fontWeights.bold,
    lineHeight: '2rem',
    fontStyle: 'normal',
    letterSpacing: '-0.01em',
    textTransform: 'none',
  },
  h3: {
    fontSize: 28,
    fontWeight: fontWeights.bold,
    lineHeight: '1.75rem',
    fontStyle: 'normal',
    textTransform: 'none',
  },
  h4: {
    fontSize: 24,
    fontWeight: fontWeights.bold,
    lineHeight: '1.5rem',
    fontStyle: 'normal',
    textTransform: 'none',
  },
  h5: {
    fontSize: 20,
    fontWeight: fontWeights.bold,
    lineHeight: '1.5rem',
    fontStyle: 'normal',
    textTransform: 'none',
  },
  h6: {
    fontSize: 18,
    fontWeight: fontWeights.bold,
    lineHeight: '1.5rem',
    fontStyle: 'normal',
    textTransform: 'none',
  },

  // ── Body & utility ──────────────────────────────────────────────────────
  body1: { fontSize: 14, lineHeight: 1.6 },
  body2: { fontSize: 12, lineHeight: 1.6 },
  caption: { fontSize: 12 },
  button: { textTransform: 'none', fontWeight: fontWeights.medium, fontSize: 14 },

  // ── Custom: bodyLight (weight 400) — sizes 12–16 ───────────────────────
  bodyLight1: createCustomVariant(12, fontWeights.light),
  bodyLight2: createCustomVariant(14, fontWeights.light),
  bodyLight3: createCustomVariant(16, fontWeights.light),

  // ── Custom: bodyRegular (weight 500) — sizes 12–16 ────────────────────
  bodyRegular1: createCustomVariant(12, fontWeights.regular),
  bodyRegular2: createCustomVariant(14, fontWeights.regular),
  bodyRegular3: createCustomVariant(16, fontWeights.regular),

  // ── Custom: bodyMedium (weight 600) — sizes 12–16 ─────────────────────
  bodyMedium1: createCustomVariant(12, fontWeights.medium),
  bodyMedium2: createCustomVariant(14, fontWeights.medium),
  bodyMedium3: createCustomVariant(16, fontWeights.medium),

  // ── Custom: bodyDark (weight 700) — sizes 12–16 ───────────────────────
  bodyDark1: createCustomVariant(12, fontWeights.bold),
  bodyDark2: createCustomVariant(14, fontWeights.bold),
  bodyDark3: createCustomVariant(16, fontWeights.bold),
}
